import useManyRemoteM1 from '@aims/shared/shared/use-many-remote-m1';
import {
  Button,
  Card,
  Empty,
  List,
  Progress,
  Table,
  Tag,
  Typography,
} from 'antd';
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PhotoCircle from '../../../components/PhotoCircle';
import settings from '../../../settings';
import {
  allContactsWithPgInfoQuery,
  extractAllContactsForAdmin,
} from '../constants';
import {
  PartnerOnboardingStatuses,
  allPartnerOnboardingsQuery,
  extractAllPartnerOnboardings,
} from '../../partner-onboarding/constants';

const { Title, Text } = Typography;

// Summary section above the confirmed grid — deliberately small; "View all" has the rest.
const IN_ONBOARDING_LIMIT = 5;

/**
 * Field workers with an onboarding still in progress under this coordinator.
 *
 * They cannot come from the confirmed grid below: that filters contacts by
 * `regionalCoordId`, which `link_field_worker_to_coordinator` only sets once the field
 * worker's MOU is signed. The onboarding record knows the coordinator from the moment it
 * is created, so this is the only place a field worker is visible before then.
 *
 * Deliberately "in onboarding" rather than "not yet confirmed": the MOU is a *stage-3*
 * task, so signing it sets `regionalCoordId` while stage-4 work remains. Someone can
 * legitimately be confirmed below and still be finishing here, and calling that
 * "not yet confirmed" would contradict the grid. Renders nothing when empty, so ordinary
 * contacts are unaffected.
 */
function FieldWorkersInOnboarding({ contact }) {
  const filters = useRef({
    coordinatorContactId: contact._id,
    partnerType: 'F',
    // Active rounds only. Without this the section also showed COMPLETE and CANCELLED
    // onboardings, which are not work in flight.
    status: 'IN_PROGRESS',
  });
  const sortBy = useRef([{ key: 'updatedAt', order: 'DESC' }]);

  // A summary section, not the full list — the table is unpaginated, so fetch a handful
  // and send anyone who needs the rest to the filtered list via "View all".
  const { data: onboardings, hasNextPage } = useManyRemoteM1({
    query: allPartnerOnboardingsQuery,
    extract: extractAllPartnerOnboardings,
    first: IN_ONBOARDING_LIMIT,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'cache-and-network',
    queryId: `in-onboarding-fws-${contact._id}`,
  });

  if (!onboardings || onboardings.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: 32 }}>
      <Title level={2} style={{ marginBottom: 4 }}>
        In Onboarding
      </Title>
      <Text type="secondary">
        {hasNextPage
          ? `First ${IN_ONBOARDING_LIMIT} field workers with onboarding in progress under this coordinator. `
          : 'Field workers with onboarding in progress under this coordinator. '}
        {/* Same scope as the section: status and partner type are both carried through,
            and the list page's coordinator picker supplies the name, so the id is all the
            link needs. `partnerType` matters because a coordinator's own onboarding
            self-references, so without it the list would also return this contact. */}
        <Link
          to={`/partner-onboarding?coordinatorContactId=${contact._id}&partnerType=F&status=IN_PROGRESS`}
        >
          {hasNextPage ? 'View all' : 'View in list'}
        </Link>
      </Text>
      <Table
        dataSource={onboardings}
        rowKey="_id"
        size="small"
        pagination={false}
        style={{ marginTop: 12 }}
        columns={[
          {
            title: 'Field Worker',
            dataIndex: 'contactId',
            render: (_, row) => {
              const name = row.contact?.fullName;
              if (!name) {
                return (
                  <Text type="secondary" italic>
                    Unknown contact
                  </Text>
                );
              }
              // Contact read is region-scoped, so only link where it would open.
              if (!row.canViewContact || !row.contactId) return name;
              return (
                <Link to={`/contact/view/${row.contactId}/partner-onboarding`}>
                  {name}
                </Link>
              );
            },
          },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 130,
            render: (val) => {
              const s = PartnerOnboardingStatuses[val];
              return s ? <Tag color={s.color}>{s.label}</Tag> : val;
            },
          },
          {
            title: 'Progress',
            key: 'progress',
            width: 150,
            render: (_, row) => {
              const done = row.tasksDoneCount ?? 0;
              const total = row.tasksTotalCount ?? 0;
              if (total === 0) return <span style={{ color: '#bbb' }}>—</span>;
              return (
                <Progress
                  percent={Math.round((done / total) * 100)}
                  size="small"
                  format={() => `${done}/${total}`}
                  status={done === total ? 'success' : 'normal'}
                />
              );
            },
          },
        ]}
      />
    </div>
  );
}

export function FieldWorker({ contact }) {
  return (
    <List.Item style={{ width: '100%' }}>
      <Card styles={{ body: { position: 'relative' } }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <PhotoCircle
            photoUrl={contact && contact.primaryThumbnailUrl}
            name={contact && contact.fullName}
            style={{
              height: 120,
              width: 120,
              padding: 'unset',
              marginBottom: 12,
            }}
          />
        </div>
        <div
          style={{
            fontSize: 14,
            textAlign: 'center',
          }}
        >
          <Text>{contact && contact.fullName}</Text>
        </div>
        <div
          style={{
            fontSize: 14,
            textAlign: 'center',
            marginBottom: 8,
          }}
        >
          {contact.peopleGroupIds?.map((pgId) => (
            <div key={pgId} style={{ marginRight: 2, marginLeft: 2 }}>
              <Link to={`/people-group/view/${pgId}/info`}>{pgId}</Link>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <Link to={`/contact/view/${contact._id}/dashboard`}>
            Contact Page
          </Link>
        </div>
      </Card>
    </List.Item>
  );
}

function FieldWorkers({ contact }) {
  const [numShown, setNumShown] = useState(8);

  const filters = useRef({
    regionalCoordId: contact && contact._id,
  });
  const sortBy = useRef([
    { key: 'firstName.keyword', order: 'ASC' },
    { key: '_score', order: 'DESC' },
  ]);

  const {
    error,
    loading,
    data: fieldWorkers,
    search: contactSearch,
    hasNextPage,
    next,
    refetch,
    reset,
  } = useManyRemoteM1({
    query: allContactsWithPgInfoQuery,
    extract: extractAllContactsForAdmin,
    first: settings.querySize,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'cache-first',
    queryId: contact._id,
  });

  if (!contact) {
    return null;
  }
  return (
    <div className="org">
      <FieldWorkersInOnboarding contact={contact} />
      <Title level={2}>Field Workers</Title>
      {error && (
        <div className="errors">
          <Text type="danger">{error}</Text>
        </div>
      )}
      <List
        grid={{ xs: 1, sm: 1, md: 2, lg: 4, xl: 5, xxl: 6, gutter: 16 }}
        dataSource={fieldWorkers
          .filter((c) => c._id !== contact._id)
          .slice(0, numShown)}
        renderItem={(item) => <FieldWorker contact={item} />}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No Field Workers"
            />
          ),
        }}
        loadMore={
          fieldWorkers.length > numShown && (
            <div className="load-more">
              <Button onClick={() => setNumShown(numShown + 8)}>
                Load More
              </Button>
            </div>
          )
        }
      />
      <style jsx>{``}</style>
    </div>
  );
}

export default FieldWorkers;
